import * as t from 'io-ts';
import { reporter } from 'io-ts-reporters';
import * as E from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/pipeable';
import axiosInstance from './network';
// TODO: catch io-ts errors with CommandBar.error() instead of throwing a browser erro
// Apply a validator and get the result in a Promise
// Source: https://www.olioapps.com/blog/checking-types-real-world-typescript/
function decodeToPromise(validator, input) {
    const result = validator.decode(input);
    return pipe(result, E.fold((_errors) => {
        const messages = reporter(result);
        const _payload = {
            error: `io-ts error -> ${messages.join('; ').toString()}`,
            errorDetail: JSON.stringify(input),
            url: window.location.href,
        };
        // FIXME: add slack logging here
        return Promise.reject(new Error(messages.join('\n')));
    }, (value) => {
        return Promise.resolve(value);
    }));
}
function decodeErr(err) {
    var _a;
    if (typeof err === 'string')
        return err;
    if (err instanceof Error)
        return err.toString();
    const data = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data;
    return data || '';
}
const GenericObject = t.type({
    id: t.union([t.number, t.string]),
});
function createObject(output, input, url) {
    const foo = async (object, onSuccess, onError) => {
        try {
            const result = await axiosInstance.post(`${url}/`, object);
            if (!!onSuccess) {
                onSuccess();
            }
            return await decodeToPromise(output, result.data);
        }
        catch (err) {
            const data = decodeErr(err);
            if (!!onError) {
                onError(JSON.stringify(data));
            }
            return Promise.reject(data);
        }
    };
    return foo;
}
function readObject(arg, url) {
    const foo = async (id, params = {}, callbacks) => {
        try {
            const result = await axiosInstance.get(`${url}/${id}/`, { params });
            if (!!(callbacks === null || callbacks === void 0 ? void 0 : callbacks.onSuccess)) {
                callbacks.onSuccess();
            }
            return await decodeToPromise(arg, result.data);
        }
        catch (err) {
            const data = decodeErr(err);
            if (!!(callbacks === null || callbacks === void 0 ? void 0 : callbacks.onError)) {
                callbacks === null || callbacks === void 0 ? void 0 : callbacks.onError(JSON.stringify(data));
            }
            return Promise.reject(data);
        }
    };
    return foo;
}
// FIXME: We can do these requests in parallel instead of in sequece
function listObject(arg, obj) {
    const foo = async (onSuccess, onError) => {
        let objects = [];
        let path = `${obj}/`;
        while (path !== null) {
            try {
                const result = await axiosInstance.get(path);
                const data = result.data;
                // Is this list paginated?
                if (data.hasOwnProperty('results')) {
                    objects = objects.concat(data['results']);
                }
                else {
                    objects = data;
                    path = null;
                }
                if (data.hasOwnProperty('next')) {
                    path = new URL(data['next']).pathname;
                }
                else {
                    path = null;
                }
                if (!!onSuccess) {
                    onSuccess();
                }
            }
            catch (err) {
                const data = decodeErr(err);
                if (!!onError) {
                    onError(JSON.stringify(data));
                }
                path = null;
            }
        }
        return objects;
    };
    return foo;
}
function updateObject(output, input, url) {
    const foo = async (object, onSuccess, onError) => {
        try {
            const result = await axiosInstance.patch(`${url}/${object.id}/`, object);
            if (!!onSuccess) {
                onSuccess();
            }
            return await decodeToPromise(output, result.data);
        }
        catch (err) {
            const data = decodeErr(err);
            if (!!onError) {
                onError(JSON.stringify(data));
            }
            return Promise.reject(data);
        }
    };
    return foo;
}
// Should change the return value to accept an object of type T (mandated to have an id field) instead of an id
function deleteObject(arg, url) {
    const foo = async (id, onSuccess, onError) => {
        try {
            const _result = await axiosInstance.delete(`${url}/${id}/`);
            if (!!onSuccess) {
                onSuccess();
            }
            return Promise.resolve();
        }
        catch (err) {
            const data = decodeErr(err);
            if (!!onError) {
                onError(JSON.stringify(data));
            }
            return Promise.reject(data);
        }
    };
    return foo;
}
function getURLString(urlArgs) {
    let urlString = '';
    if (urlArgs) {
        Object.keys(urlArgs).forEach((key, i) => {
            if (i === 0) {
                urlString = `?${key}=${urlArgs[key]}`;
            }
            else {
                urlString = `${urlString}&${key}=${urlArgs[key]}`;
            }
        });
    }
    urlString = urlString.replace(/\+/g, '%2B');
    return urlString;
}
function readObjectDetail(arg, url, detail) {
    const foo = async (id, urlArgs, onSuccess, onError) => {
        const urlString = getURLString(urlArgs);
        try {
            const result = await axiosInstance.get(`${url}/${id}/${detail}/${urlString}`);
            if (!!onSuccess) {
                onSuccess();
            }
            return await decodeToPromise(arg, result.data);
        }
        catch (err) {
            const data = decodeErr(err);
            if (!!onError) {
                onError(JSON.stringify(data));
            }
            return Promise.reject(data);
        }
    };
    return foo;
}
function updateObjectDetail(output, input, url, detail) {
    const foo = async (object, urlArgs, onSuccess, onError) => {
        const urlString = getURLString(urlArgs);
        try {
            const result = await axiosInstance.patch(`${url}/${object.id}/${detail}/${urlString}`, object);
            if (!!onSuccess) {
                onSuccess();
            }
            return await decodeToPromise(output, result.data);
        }
        catch (err) {
            const data = decodeErr(err);
            if (!!onError) {
                onError(JSON.stringify(data));
            }
            return Promise.reject(data);
        }
    };
    return foo;
}
function createObjectDetail(output, input, url, detail) {
    const foo = async (object, onSuccess, onError, urlArgs) => {
        const urlString = getURLString(urlArgs);
        try {
            const result = await axiosInstance.post(`${url}/${object.id}/${detail}/${urlString}`, object);
            if (!!onSuccess) {
                onSuccess();
            }
            return await decodeToPromise(output, result.data);
        }
        catch (err) {
            const data = decodeErr(err);
            if (!!onError) {
                onError(JSON.stringify(data));
            }
            return Promise.reject(data);
        }
    };
    return foo;
}
async function loadIDList(ids, klass, method = 'read', urlArgs) {
    const ignoreRejects = (p) => {
        return p.catch((_e) => {
            return undefined;
        });
    };
    const promises = ids.map(async (id) => {
        return await klass[method](id, urlArgs);
    });
    const data = await Promise.all(promises.map(ignoreRejects));
    const filteredData = data.filter((a) => {
        return a !== undefined;
    });
    return filteredData;
}
function read(arg, url) {
    const foo = async (params = {}, callbacks) => {
        try {
            const result = await axiosInstance.get(`${url}/`, { params });
            if (!!(callbacks === null || callbacks === void 0 ? void 0 : callbacks.onSuccess)) {
                callbacks.onSuccess();
            }
            return await decodeToPromise(arg, result.data);
        }
        catch (err) {
            const data = decodeErr(err);
            if (!!(callbacks === null || callbacks === void 0 ? void 0 : callbacks.onError)) {
                callbacks === null || callbacks === void 0 ? void 0 : callbacks.onError(JSON.stringify(data));
            }
            return Promise.reject(data);
        }
    };
    return foo;
}
export { createObject, createObjectDetail, read, readObject, listObject, updateObject, deleteObject, GenericObject, readObjectDetail, updateObjectDetail, loadIDList, decodeToPromise, };
//# sourceMappingURL=data:application/json;base64,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